import { useCallback, type UIEvent } from "react";
import { useSelector } from "react-redux";
import PlusFilledIcon from "../../../../../../../assets/svg/plus-filled-icon";
import PulseLoader from "../../../../../../../components/Loader/PulseLoader";
import { VIEW_SELECTOR_OPTIONS } from "../utils/constant";
import { HelpdeskViewItem } from "../utils/types";
import CompletedIcon from "../../../../../../../assets/svg/completed-icon";
import { useAppDispatch } from "../../../../../../../redux/store";
import type { RootState } from "../../../../../../../redux/store";
import { setDrawer } from "../../../../../../../redux/slices/drawerSlice";
import CrossIcon from "../../../../../../../assets/svg/v1/cross.icon";
import EditIcon from "../../../../../../../assets/svg/edit-icon";
import { shouldShowHelpdeskViewSelector } from "../../../utils/function";
import { getClientDomain } from "../../../../../../../utils/functions/client-code";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";

interface ViewTabsToolbarProps {
  activeTab: string;
  view: string;
  setView: (view: string) => void;
  selectedViewId?: string;
  setSelectedViewId?: React.Dispatch<React.SetStateAction<string>>;
  onDeleteView?: (view: HelpdeskViewItem) => void;
  hasMoreViews?: boolean;
  isLoadingMoreViews?: boolean;
  onLoadMoreViews?: () => void | Promise<void>;
  onExportClick?: () => void;
}

export const ViewTabsToolbar = ({
  activeTab,
  view,
  setView,
  selectedViewId = "all_tickets",
  setSelectedViewId,
  onDeleteView,
  hasMoreViews = false,
  isLoadingMoreViews = false,
  onLoadMoreViews,
  onExportClick,
}: ViewTabsToolbarProps) => {
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region ?? "UK");
  const dispatch = useAppDispatch();
  const helpdeskViewsFromStore = useSelector(
    (state: RootState) => state.helpdesk.helpdeskViews
  );

  const clientCode = getClientDomain();

  const helpdeskViews = helpdeskViewsFromStore ?? [];

  const handleTabsScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const element = event.currentTarget;
      if (element.scrollWidth - element.scrollLeft - element.clientWidth > 60) {
        return;
      }
      if (!hasMoreViews || isLoadingMoreViews) return;
      void onLoadMoreViews?.();
    },
    [hasMoreViews, isLoadingMoreViews, onLoadMoreViews]
  );

  const handleSelectTab = useCallback(
    (tabId: string) => {
      setSelectedViewId?.(tabId);
    },
    [setSelectedViewId]
  );

  return (
    <div className="pb-4 flex items-center justify-between">
      <div className="flex items-center gap-[6px] min-w-0">
        {(() => {
          const isSelected = selectedViewId === "all_tickets";
          return (
            <button
              onClick={() => handleSelectTab("all_tickets")}
              className={`group flex items-center gap-[2px] h-[26px] px-[6px] rounded-md border transition-colors cursor-pointer shrink-0 ${
                isSelected
                  ? "bg-crm-card-highlight-2 dark:bg-crm-card-highlight-2-dk border-crm-card-border dark:border-crm-card-border-dk"
                  : "bg-transparent border-border-gray dark:border-border-gray-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
              }`}
            >
              <span className="w-[14px] h-[14px] flex items-center justify-center shrink-0">
                <CompletedIcon className="w-[14px] h-[14px] shrink-0 text-verba-blue dark:text-verba-blue-dk" />
              </span>
              <span className="text-sm whitespace-nowrap text-verba-indigo dark:text-verba-indigo-dk">
                All {helpdeskLabels.ticketPlural}
              </span>
            </button>
          );
        })()}

        {(helpdeskViews?.length ?? 0) > 0 &&
          shouldShowHelpdeskViewSelector(clientCode, activeTab) && (
            <>
              <div className="h-4 w-px bg-border-gray dark:bg-border-gray-dk shrink-0" />

              <div
                onScroll={handleTabsScroll}
                className="flex items-center gap-[6px] max-w-[850px] overflow-x-auto overflow-y-hidden no-scrollbar"
              >
                {helpdeskViews.map((tab) => {
                  const isSelected = selectedViewId === tab?.id;
                  return (
                    <button
                      key={tab?.id}
                      onClick={() => handleSelectTab(tab?.id)}
                      className={`group flex items-center h-[26px] px-[6px] rounded-md border transition-all duration-150 cursor-pointer shrink-0 ${
                        isSelected
                          ? "gap-2 bg-crm-card-highlight-2 dark:bg-crm-card-highlight-2-dk border-crm-card-border dark:border-crm-card-border-dk"
                          : "gap-0 hover:gap-2 focus-within:gap-2 bg-transparent border-border-gray dark:border-border-gray-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        {tab?.emoji_icon && (
                          <span className="text-md leading-none shrink-0">
                            {tab?.emoji_icon}
                          </span>
                        )}
                        <span className="text-sm whitespace-nowrap text-verba-indigo dark:text-verba-indigo-dk">
                          {tab?.name}
                        </span>
                      </div>

                      <div
                        className={`flex items-center shrink-0 transition-[gap] duration-150 ${
                          isSelected
                            ? "gap-1"
                            : "gap-0 group-hover:gap-1 group-focus-within:gap-1"
                        }`}
                      >
                        <span
                          className={`h-[14px] min-w-0 overflow-hidden shrink-0 flex items-center justify-center transition-[width,opacity] duration-150 mb-px ${
                            isSelected
                              ? "w-[14px] opacity-100 pointer-events-auto"
                              : "w-0 opacity-0 pointer-events-none group-hover:w-[14px] group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:w-[14px] group-focus-within:opacity-100 group-focus-within:pointer-events-auto"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              dispatch(
                                setDrawer({
                                  open: true,
                                  type: "helpdesk_edit_view",
                                  id: tab.id,
                                })
                              );
                            }}
                            className={`flex items-center justify-center p-0 border-0 bg-transparent cursor-pointer`}
                            aria-label={`Edit view ${tab.name}`}
                          >
                            <EditIcon className="w-[14px] h-[14px] shrink-0 text-dark-gray dark:text-dark-gray-dk" />
                          </button>
                        </span>

                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteView?.(tab);
                          }}
                          className={`cursor-pointer h-[10px] min-w-0 overflow-hidden shrink-0 flex items-center justify-center rounded-full bg-dark-gray dark:bg-dark-gray-dk hover:text-red-error dark:hover:text-red-error-dk transition-[width,opacity] duration-150 ${
                            isSelected
                              ? "w-[10px] opacity-100 pointer-events-auto"
                              : "w-0 opacity-0 pointer-events-none group-hover:w-[10px] group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:w-[10px] group-focus-within:opacity-100 group-focus-within:pointer-events-auto"
                          }`}
                        >
                          <CrossIcon className="w-[6px] h-[6px] shrink-0 text-pure-white dark:text-pure-white-dk stroke-3" />
                        </span>
                      </div>
                    </button>
                  );
                })}
                {isLoadingMoreViews && (
                  <div className="flex items-center justify-center shrink-0 pl-4 pr-4">
                    <PulseLoader size={6} />
                  </div>
                )}
              </div>
            </>
          )}
        {shouldShowHelpdeskViewSelector(clientCode, activeTab) && (
          <>
            <div className="h-4 w-px bg-border-gray dark:bg-border-gray-dk shrink-0" />
            <button
              className="flex items-center gap-0.5 cursor-pointer shrink-0"
              onClick={() => {
                dispatch(setDrawer({ open: true, type: "helpdesk_view" }));
              }}
            >
              <PlusFilledIcon className="w-4 h-4 shrink-0 text-verba-blue dark:text-verba-blue-dk" />
              <span className="text-md font-medium text-verba-blue dark:text-verba-blue-dk whitespace-nowrap hover:underline">
                View
              </span>
            </button>
          </>
        )}
        {onExportClick &&
          shouldShowHelpdeskViewSelector(clientCode, activeTab) && (
            <>
              <div className="h-4 w-px bg-border-gray dark:bg-border-gray-dk shrink-0" />
              <button
                onClick={onExportClick}
                className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk border border-border-gray dark:border-border-gray-dk rounded-md px-3 py-1 hover:bg-hover-effect dark:hover:bg-hover-effect-dk whitespace-nowrap shrink-0"
              >
                Export
              </button>
            </>
          )}
      </div>

      <div className="w-max flex items-center p-0.5 border border-border-gray dark:border-border-gray-dk rounded-md shrink-0 ml-3">
        {VIEW_SELECTOR_OPTIONS?.map(({ id, icon: Icon, label }) => (
          <button
            key={id}
            className={`pl-1 pr-2 py-0.5 cursor-pointer rounded-sm ${
              view === id && "bg-verba-indigo dark:bg-verba-indigo-dk"
            }`}
            onClick={() => setView(id)}
            title={label}
          >
            <div className="flex items-center gap-0.5">
              <Icon
                className={`w-[18px] h-[18px] ${
                  view === id
                    ? "text-pure-white dark:text-pure-white-dk"
                    : "text-verba-indigo dark:text-verba-indigo-dk"
                }`}
              />
              <span
                className={`text-md whitespace-nowrap ${
                  view === id
                    ? "font-semibold text-pure-white dark:text-pure-white-dk"
                    : "font-normal text-verba-indigo dark:text-verba-indigo-dk"
                }`}
              >
                {label}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
